/**
 * Notification System (v2 - Supabase Realtime + Database)
 * 
 * Provides type definitions and factory functions for notifications.
 * Storage is now handled by the database, with edge function for creation.
 * Keeps localStorage helpers for migration purposes.
 */

import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { debug } from "@/lib/debug";

// Notification types
export type NotificationType =
  | 'creator_fees_claimable'
  | 'creator_fees_claimed'
  | 'token_watchlist_movement'
  | 'followed_creator_launch'
  | 'own_token_launch'
  | 'watchlisted_token_trending'
  | 'withdrawal_completed'
  | 'deposit_received'
  | 'trade_executed'
  | 'swap_completed'
  | 'new_comment_on_token'
  | 'new_like_on_token'
  | 'new_follower'
  | 'fee_share_received'
  // Verification notification types
  | 'verification_submitted'
  | 'verification_under_review'
  | 'verification_approved'
  | 'verification_rejected';

export type NotificationCategory = 'money' | 'social';

// Frontend notification interface (camelCase)
export interface AppNotification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  cta: {
    label: string;
    path: string;
  };
  // Optional entity references
  tokenMint?: string;
  tokenName?: string;
  tokenSymbol?: string;
  tokenImage?: string;
  creatorAddress?: string;
  creatorName?: string;
  // Extra data for display
  value?: number;
  txSignature?: string;
  dedupKey?: string;
}

// Database notification interface (snake_case)
export interface DbNotification {
  id: string;
  wallet_address: string;
  type: string;
  category: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  cta_label: string;
  cta_path: string;
  token_mint?: string | null;
  token_name?: string | null;
  token_symbol?: string | null;
  token_image?: string | null;
  creator_address?: string | null;
  creator_name?: string | null;
  value?: number | null;
  tx_signature?: string | null;
  dedup_key?: string | null;
}

// ========== TYPE CONVERSION ==========

export function mapDbToAppNotification(db: DbNotification): AppNotification {
  return {
    id: db.id,
    type: db.type as NotificationType,
    category: db.category as NotificationCategory,
    title: db.title,
    message: db.message,
    timestamp: new Date(db.created_at),
    read: db.read,
    cta: {
      label: db.cta_label,
      path: db.cta_path,
    },
    tokenMint: db.token_mint || undefined,
    tokenName: db.token_name || undefined,
    tokenSymbol: db.token_symbol || undefined,
    tokenImage: db.token_image || undefined,
    creatorAddress: db.creator_address || undefined,
    creatorName: db.creator_name || undefined,
    value: db.value || undefined,
    txSignature: db.tx_signature || undefined,
    dedupKey: db.dedup_key || undefined,
  };
}

// ========== DATABASE OPERATIONS ==========

export async function fetchNotifications(walletAddress: string, limit = 50): Promise<AppNotification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('wallet_address', walletAddress)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[Notifications] Fetch error:', error);
    return [];
  }

  return (data || []).map(mapDbToAppNotification);
}

export async function markNotificationRead(notificationId: string, walletAddress: string): Promise<boolean> {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notificationId)
    .eq('wallet_address', walletAddress);

  if (error) {
    console.error('[Notifications] Mark read error:', error);
    return false;
  }
  
  trackEvent('notification_mark_read', { id: notificationId });
  return true;
}

export async function markAllNotificationsRead(walletAddress: string): Promise<boolean> {
  const { error, count } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('wallet_address', walletAddress)
    .eq('read', false);

  if (error) {
    console.error('[Notifications] Mark all read error:', error);
    return false;
  }

  trackEvent('notification_mark_all_read', { count: count || 0 });
  return true;
}

export async function deleteAllNotifications(walletAddress: string): Promise<boolean> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('wallet_address', walletAddress);

  if (error) {
    console.error('[Notifications] Delete all error:', error);
    return false;
  }

  trackEvent('notification_clear_all');
  return true;
}

// ========== EDGE FUNCTION CALLS ==========

interface CreateNotificationPayload {
  wallet_address: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  cta_label: string;
  cta_path: string;
  token_mint?: string;
  token_name?: string;
  token_symbol?: string;
  token_image?: string;
  creator_address?: string;
  creator_name?: string;
  value?: number;
  tx_signature?: string;
  dedup_key: string;
}

export async function createNotificationInDb(payload: CreateNotificationPayload): Promise<boolean> {
  try {
    const { error } = await supabase.functions.invoke('create-notification', {
      body: payload,
    });

    if (error) {
      console.error('[Notifications] Create notification error:', error);
      return false;
    }

    trackEvent('notification_created', { 
      type: payload.type, 
      category: payload.category,
      dedup_key: payload.dedup_key,
    });
    
    return true;
  } catch (err) {
    console.error('[Notifications] Create notification exception:', err);
    return false;
  }
}

// ========== DEDUP KEY HELPERS ==========

function getTodayDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function generateDedupKey(type: NotificationType, identifier: string, useDateSuffix = true): string {
  if (useDateSuffix) {
    return `${type}:${identifier}:${getTodayDateString()}`;
  }
  return `${type}:${identifier}`;
}

// ========== NOTIFICATION FACTORIES ==========

export async function createCreatorFeesNotification(
  walletAddress: string,
  totalClaimable: number,
  poolCount: number
): Promise<boolean> {
  const dedupKey = generateDedupKey('creator_fees_claimable', walletAddress);

  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'creator_fees_claimable',
    category: 'money',
    title: 'Creator Fees Ready',
    message: `You have ${totalClaimable.toFixed(4)} SOL claimable across ${poolCount} pool${poolCount > 1 ? 's' : ''}`,
    cta_label: 'Claim Fees',
    cta_path: '/creator/fees',
    value: totalClaimable,
    dedup_key: dedupKey,
  });
}

export async function createCreatorFeesClaimedNotification(
  walletAddress: string,
  amountSol: number,
  signature: string,
  poolAddress?: string
): Promise<boolean> {
  // Use signature for true deduplication per transaction
  const dedupKey = generateDedupKey('creator_fees_claimed', signature, false);

  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'creator_fees_claimed',
    category: 'money',
    title: 'Rewards Claimed!',
    message: `You claimed ${amountSol.toFixed(4)} SOL in creator rewards`,
    cta_label: 'View Rewards',
    cta_path: `/creator/${walletAddress}/rewards`,
    value: amountSol,
    tx_signature: signature,
    dedup_key: dedupKey,
  });
}

export async function createFeeShareReceivedNotification(
  recipientWallet: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null,
  amountLamports: number,
  creatorName: string,
  txSignature: string
): Promise<boolean> {
  const amountSol = amountLamports / 1e9;
  const dedupKey = generateDedupKey('fee_share_received', txSignature, false);

  return createNotificationInDb({
    wallet_address: recipientWallet,
    type: 'fee_share_received',
    category: 'money',
    title: 'You earned shared fees',
    message: `${amountSol.toFixed(6)} SOL shared with you from ${tokenName || tokenMint.slice(0, 8)}`,
    cta_label: 'View Rewards',
    cta_path: '/creator/fees',
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage,
    creator_name: creatorName,
    value: amountSol,
    tx_signature: txSignature,
    dedup_key: dedupKey,
  });
}

export async function createTokenMovementNotification(
  walletAddress: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null,
  priceChange: number
): Promise<boolean> {
  const dedupKey = generateDedupKey('token_watchlist_movement', tokenMint);
  const direction = priceChange > 0 ? 'up' : 'down';
  
  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'token_watchlist_movement',
    category: 'money',
    title: `${tokenSymbol} ${direction === 'up' ? '📈' : '📉'} ${Math.abs(priceChange).toFixed(1)}%`,
    message: `${tokenName} is ${direction} ${Math.abs(priceChange).toFixed(1)}% in the last 24h`,
    cta_label: 'View Coin',
    cta_path: `/token/${tokenMint}`,
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage || undefined,
    value: priceChange,
    dedup_key: dedupKey,
  });
}

export async function createFollowedCreatorLaunchNotification(
  walletAddress: string,
  creatorAddress: string,
  creatorName: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null
): Promise<boolean> {
  // Use tokenMint without date suffix for true deduplication per token
  const dedupKey = generateDedupKey('followed_creator_launch', tokenMint, false);
  
  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'followed_creator_launch',
    category: 'social',
    title: `🚀 ${creatorName} launched ${tokenSymbol}`,
    message: `New coin: ${tokenName}`,
    cta_label: 'View Coin',
    cta_path: `/token/${tokenMint}`,
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage || undefined,
    creator_address: creatorAddress,
    creator_name: creatorName,
    dedup_key: dedupKey,
  });
}

export async function createOwnTokenLaunchNotification(
  walletAddress: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null
): Promise<boolean> {
  // Use tokenMint without date suffix for true deduplication per token
  const dedupKey = generateDedupKey('own_token_launch', tokenMint, false);
  
  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'own_token_launch',
    category: 'money',
    title: `🚀 ${tokenSymbol} is Live!`,
    message: `Your coin "${tokenName}" was launched successfully`,
    cta_label: 'View Coin',
    cta_path: `/token/${tokenMint}`,
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage || undefined,
    dedup_key: dedupKey,
  });
}

export async function createTrendingTokenNotification(
  walletAddress: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null
): Promise<boolean> {
  const dedupKey = generateDedupKey('watchlisted_token_trending', tokenMint);
  
  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'watchlisted_token_trending',
    category: 'social',
    title: 'Coin Trending 🔥',
    message: `${tokenName} (${tokenSymbol}) is now trending`,
    cta_label: 'View Coin',
    cta_path: `/token/${tokenMint}`,
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage || undefined,
    dedup_key: dedupKey,
  });
}

// ========== TRANSACTION NOTIFICATIONS ==========

// Deposit and withdrawal notifications are written exclusively by the
// helius-webhook edge function (supabase/functions/helius-webhook/index.ts).
// Frontend code does NOT persist these notifications: previously a parallel
// client-side path with synthetic timestamp signatures could not dedupe
// against the webhook's real-tx-signature dedup_key, producing duplicate rows.

/**
 * Build an AppNotification for a creator fee claim without hitting the DB.
 * Used for optimistic UI updates before the edge function round-trip completes.
 * Mirrors the same title/message/dedupKey format as createCreatorFeesClaimedNotification().
 */
export function buildCreatorFeesClaimedAppNotification(
  walletAddress: string,
  amountSol: number,
  signature: string,
  poolAddress?: string
): AppNotification {
  const dedupKey = generateDedupKey('creator_fees_claimed', signature, false);

  return {
    id: `optimistic_${signature}`,
    type: 'creator_fees_claimed',
    category: 'money',
    title: 'Rewards Claimed!',
    message: `You claimed ${amountSol.toFixed(4)} SOL in creator rewards`,
    timestamp: new Date(),
    read: false,
    cta: {
      label: 'View Rewards',
      path: `/creator/${walletAddress}/rewards`,
    },
    value: amountSol,
    txSignature: signature,
    dedupKey,
  };
}

export async function createTradeNotification(
  walletAddress: string,
  tradeType: 'buy' | 'sell',
  tokenSymbol: string,
  amount: number,
  signature: string,
  tokenMint?: string
): Promise<boolean> {
  // Use signature for true deduplication per transaction
  const dedupKey = generateDedupKey('trade_executed', signature, false);
  const isBuy = tradeType === 'buy';

  // Deep-link to Activity tab with transaction highlighted
  const ctaPath = `/portfolio?tab=activity&tx=${encodeURIComponent(signature)}`;

  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'trade_executed',
    category: 'money',
    title: isBuy ? `Bought ${tokenSymbol}` : `Sold ${tokenSymbol}`,
    message: isBuy
      ? `Purchased ${amount.toLocaleString()} ${tokenSymbol}`
      : `Sold for ${amount.toFixed(4)} SOL`,
    cta_label: 'View in Portfolio',
    cta_path: ctaPath,
    token_mint: tokenMint,
    token_symbol: tokenSymbol,
    value: amount,
    tx_signature: signature,
    dedup_key: dedupKey,
  });
}

export async function createSwapNotification(
  walletAddress: string,
  inputSymbol: string,
  outputSymbol: string,
  inputAmount: string,
  outputAmount: string,
  signature: string
): Promise<boolean> {
  const dedupKey = generateDedupKey('swap_completed', signature, false);
  const ctaPath = `https://solscan.io/tx/${signature}`;

  return createNotificationInDb({
    wallet_address: walletAddress,
    type: 'swap_completed',
    category: 'money',
    title: `Swapped ${inputSymbol} for ${outputSymbol}`,
    message: `${inputAmount} ${inputSymbol} swapped for ${outputAmount} ${outputSymbol}`,
    cta_label: 'View on Solscan',
    cta_path: ctaPath,
    tx_signature: signature,
    dedup_key: dedupKey,
  });
}

export function buildSwapAppNotification(
  inputSymbol: string,
  outputSymbol: string,
  inputAmount: string,
  outputAmount: string,
  signature: string
): AppNotification {
  const dedupKey = generateDedupKey('swap_completed', signature, false);
  const ctaPath = `https://solscan.io/tx/${signature}`;

  return {
    id: `optimistic_${signature}`,
    type: 'swap_completed',
    category: 'money',
    title: `Swapped ${inputSymbol} for ${outputSymbol}`,
    message: `${inputAmount} ${inputSymbol} swapped for ${outputAmount} ${outputSymbol}`,
    timestamp: new Date(),
    read: false,
    cta: {
      label: 'View on Solscan',
      path: ctaPath,
    },
    txSignature: signature,
    dedupKey,
  };
}

// ========== USERNAME LOOKUP ==========

/**
 * Look up a user's username from creator_profiles.
 * Every registered user has a username (mandatory on signup).
 */
async function lookupUsername(walletAddress: string): Promise<string> {
  try {
    const { data } = await supabase
      .from('creator_profiles')
      .select('username')
      .eq('wallet_address', walletAddress)
      .maybeSingle();

    if (data?.username) return data.username;
  } catch (err) {
    console.error('[Notifications] Username lookup failed:', err);
  }
  // Should not happen for registered users
  return 'Unknown';
}

/**
 * Batch look up usernames for multiple wallet addresses.
 * Returns a Map of walletAddress -> username.
 */
export async function lookupUsernames(walletAddresses: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  if (walletAddresses.length === 0) return map;

  try {
    const { data } = await supabase
      .from('creator_profiles')
      .select('wallet_address, username')
      .in('wallet_address', walletAddresses);

    if (data) {
      for (const p of data) {
        if (p.username) map.set(p.wallet_address, p.username);
      }
    }
  } catch (err) {
    console.error('[Notifications] Batch username lookup failed:', err);
  }
  return map;
}

// ========== SOCIAL NOTIFICATION FACTORIES ==========

export async function createCommentNotification(
  creatorWallet: string,
  commenterAddress: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null
): Promise<boolean> {
  const dedupKey = generateDedupKey('new_comment_on_token', `${tokenMint}:${commenterAddress}:${Date.now()}`);
  const commenterName = await lookupUsername(commenterAddress);

  return createNotificationInDb({
    wallet_address: creatorWallet,
    type: 'new_comment_on_token',
    category: 'social',
    title: `New comment on ${tokenSymbol}`,
    message: `${commenterName} commented on your coin`,
    cta_label: 'View Coin',
    cta_path: `/token/${tokenMint}`,
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage || undefined,
    creator_address: commenterAddress,
    creator_name: commenterName,
    dedup_key: dedupKey,
  });
}

export async function createLikeNotification(
  creatorWallet: string,
  likerAddress: string,
  tokenMint: string,
  tokenName: string,
  tokenSymbol: string,
  tokenImage: string | null
): Promise<boolean> {
  const dedupKey = generateDedupKey('new_like_on_token', `${tokenMint}:${likerAddress}:${Date.now()}`);
  const likerName = await lookupUsername(likerAddress);

  return createNotificationInDb({
    wallet_address: creatorWallet,
    type: 'new_like_on_token',
    category: 'social',
    title: `${likerName} liked ${tokenSymbol}`,
    message: `Your coin received a new like`,
    cta_label: 'View Coin',
    cta_path: `/token/${tokenMint}`,
    token_mint: tokenMint,
    token_name: tokenName,
    token_symbol: tokenSymbol,
    token_image: tokenImage || undefined,
    creator_address: likerAddress,
    creator_name: likerName,
    dedup_key: dedupKey,
  });
}

export async function createFollowerNotification(
  followedWallet: string,
  followerAddress: string
): Promise<boolean> {
  const dedupKey = generateDedupKey('new_follower', `${followerAddress}:${Date.now()}`);
  const followerName = await lookupUsername(followerAddress);

  return createNotificationInDb({
    wallet_address: followedWallet,
    type: 'new_follower',
    category: 'social',
    title: 'New follower',
    message: `${followerName} started following you`,
    cta_label: 'View Profile',
    cta_path: `/creator/${followerAddress}`,
    creator_address: followerAddress,
    creator_name: followerName,
    dedup_key: dedupKey,
  });
}

// ========== LEGACY LOCALSTORAGE (for migration) ==========

const NOTIFICATIONS_KEY = 'memelaunch_notifications';
const GENERIC_WALLET = '__anonymous__';
const MIGRATION_KEY = 'memelaunch_notifications_migrated';

function getStorageKey(wallet?: string | null): string {
  return `${NOTIFICATIONS_KEY}_${wallet || GENERIC_WALLET}`;
}

export function getLocalStorageNotifications(wallet?: string | null): AppNotification[] {
  try {
    const key = getStorageKey(wallet);
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return parsed.map((n: Record<string, unknown>) => ({
      ...n,
      timestamp: new Date(n.timestamp as string),
    }));
  } catch {
    return [];
  }
}

export function clearLocalStorageNotifications(wallet: string | null): void {
  const key = getStorageKey(wallet);
  localStorage.removeItem(key);
}

export function isNotificationsMigrated(wallet: string): boolean {
  return localStorage.getItem(`${MIGRATION_KEY}_${wallet}`) === 'true';
}

export function markNotificationsMigrated(wallet: string): void {
  localStorage.setItem(`${MIGRATION_KEY}_${wallet}`, 'true');
}

// Migrate localStorage notifications to database
export async function migrateLocalStorageNotifications(wallet: string): Promise<void> {
  if (isNotificationsMigrated(wallet)) {
    debug.trace('[Notifications] Already migrated for wallet:', wallet.slice(0, 8));
    return;
  }

  const localNotifications = getLocalStorageNotifications(wallet);
  if (localNotifications.length === 0) {
    markNotificationsMigrated(wallet);
    debug.trace('[Notifications] No local notifications to migrate');
    return;
  }

  debug.trace('[Notifications] Migrating', localNotifications.length, 'notifications from localStorage');

  // Migrate each notification (edge function handles deduplication)
  for (const notification of localNotifications) {
    try {
      await createNotificationInDb({
        wallet_address: wallet,
        type: notification.type,
        category: notification.category,
        title: notification.title,
        message: notification.message,
        cta_label: notification.cta.label,
        cta_path: notification.cta.path,
        token_mint: notification.tokenMint,
        token_name: notification.tokenName,
        token_symbol: notification.tokenSymbol,
        token_image: notification.tokenImage,
        creator_address: notification.creatorAddress,
        creator_name: notification.creatorName,
        value: notification.value,
        tx_signature: notification.txSignature,
        dedup_key: notification.dedupKey || notification.id,
      });
    } catch (err) {
      console.error('[Notifications] Migration error for notification:', notification.id, err);
    }
  }

  // Clear localStorage and mark as migrated
  clearLocalStorageNotifications(wallet);
  markNotificationsMigrated(wallet);
  debug.trace('[Notifications] Migration complete for wallet:', wallet.slice(0, 8));
}
